import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { JOURNALIST_PROMPT_KEYS } from "@kritaka/core";
import { useEffect, useRef, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
import { PromptEditor } from "./PromptEditor.js";
const TABS = [
    {
        key: "shared.methodology",
        label: "Shared methodology",
        blurb: "The workspace's vault methodology — referenced by every role template via {{shared.methodology}}.",
    },
    {
        key: "role.extraction",
        label: "Field reporter",
        blurb: "15-minute channel-firehose transcription pass.",
    },
    {
        key: "role.digest",
        label: "Daily digest",
        blurb: "Once-a-day fold of raw 15-min entries into a single narrative.",
    },
    {
        key: "role.synthesis",
        label: "Synthesis",
        blurb: "Promotes digest entries into the semantic / procedural / identity layers.",
    },
    {
        key: "role.knowledge_query",
        label: "Reference desk",
        blurb: "Powers per-agent briefings + ad-hoc knowledge queries.",
    },
];
// Custom event broadcast on save/reset so any open Advanced section can
// re-fetch its composed prompt against the latest template values.
export const PROMPT_CHANGED_EVENT = "kritaka:journalist-prompt-changed";
function emitPromptChanged(detail) {
    if (typeof window === "undefined")
        return;
    window.dispatchEvent(new CustomEvent(PROMPT_CHANGED_EVENT, { detail }));
}
export function JournalistPromptsPanel({ workspaceId, inline = false, onClose }) {
    const ctx = useDataContext();
    const [templates, setTemplates] = useState(null);
    const [activeKey, setActiveKey] = useState("shared.methodology");
    // Local edits per key — flushed to the backend on debounce. Keeping
    // them in a separate map lets the operator switch tabs without losing
    // unsaved changes.
    const [drafts, setDrafts] = useState({});
    const [savedAt, setSavedAt] = useState({});
    const [savingKey, setSavingKey] = useState(null);
    // Two-click reset: first click arms `resetArmed`; second click on the
    // same key fires the POST. Switching tabs disarms.
    const [resetArmed, setResetArmed] = useState(null);
    const [resetting, setResetting] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const debounceRef = useRef({});
    useEffect(() => {
        let cancelled = false;
        ctx.hub
            .getJournalistPromptTemplates(workspaceId)
            .then((t) => {
            if (!cancelled)
                setTemplates(t);
        })
            .catch((e) => {
            if (!cancelled)
                setError(e instanceof Error ? e.message : String(e));
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspaceId]);
    function valueFor(key) {
        if (key in drafts)
            return drafts[key];
        return templates?.[key]?.value ?? "";
    }
    function onChange(key, next) {
        setDrafts((d) => ({ ...d, [key]: next }));
        if (debounceRef.current[key])
            clearTimeout(debounceRef.current[key]);
        debounceRef.current[key] = setTimeout(() => {
            void flushKey(key);
        }, 800);
    }
    async function flushKey(key) {
        const next = drafts[key];
        if (next === undefined)
            return;
        if (templates && next === templates[key]?.value)
            return;
        setSavingKey(key);
        setError(null);
        try {
            const entry = await ctx.hub.putJournalistPromptTemplate(workspaceId, key, next);
            setTemplates((t) => (t ? { ...t, [key]: entry } : t));
            // Only drop the draft if the user hasn't typed past the value
            // we just saved while the PUT was in flight. Otherwise we'd
            // clobber their newer text on save-completion (KTK-301).
            setDrafts((d) => {
                if (d[key] !== next)
                    return d;
                const copy = { ...d };
                delete copy[key];
                return copy;
            });
            setSavedAt((s) => ({ ...s, [key]: new Date().toISOString() }));
            emitPromptChanged({ workspaceId, key });
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setSavingKey(null);
        }
    }
    function onBlur(key) {
        if (debounceRef.current[key]) {
            clearTimeout(debounceRef.current[key]);
            debounceRef.current[key] = undefined;
        }
        void flushKey(key);
    }
    async function onResetClick(key) {
        if (resetArmed !== key) {
            setResetArmed(key);
            return;
        }
        setResetArmed(null);
        if (debounceRef.current[key]) {
            clearTimeout(debounceRef.current[key]);
            debounceRef.current[key] = undefined;
        }
        setResetting(key);
        setError(null);
        try {
            const entry = await ctx.hub.resetJournalistPromptTemplate(workspaceId, key);
            setTemplates((t) => (t ? { ...t, [key]: entry } : t));
            setDrafts((d) => {
                const copy = { ...d };
                delete copy[key];
                return copy;
            });
            setSavedAt((s) => ({ ...s, [key]: new Date().toISOString() }));
            emitPromptChanged({ workspaceId, key });
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setResetting(null);
        }
    }
    const activeTab = TABS.find((t) => t.key === activeKey);
    const lastSavedIso = savedAt[activeKey] ?? templates?.[activeKey]?.updated_at ?? null;
    const body = (_jsxs(_Fragment, { children: [_jsxs("header", { className: "ws-sect__head", children: [_jsx("h2", { className: "ws-sect__h", children: "Prompts" }), _jsxs("p", { className: "ws-sect__sub", children: ["Hub-stored prompt templates for every journalist role. Each role template can reference", " ", _jsx("span", { className: "ws-mono", children: `{{shared.methodology}}` }), " plus runtime variables like", " ", _jsx("span", { className: "ws-mono", children: `{{workspace_name}}` }), ",", " ", _jsx("span", { className: "ws-mono", children: `{{current_iso}}` }), ", and", " ", _jsx("span", { className: "ws-mono", children: `{{schema}}` }), ". Substitution happens server-side at render time \u2014 composed previews live in the Advanced section under each manual trigger above."] })] }), loading ? (_jsx("div", { className: "ws-card", children: _jsx("span", { className: "ws-mono", children: "Loading\u2026" }) })) : (_jsxs("div", { className: "ws-card", children: [error && (_jsx("div", { style: {
                            color: "var(--error)",
                            fontSize: 12,
                            padding: "8px 10px",
                            border: "1px solid rgba(250,82,82,0.3)",
                            borderRadius: 6,
                        }, children: error })), _jsx("div", { style: {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 4,
                            borderBottom: "1px solid var(--hairline)",
                            paddingBottom: 6,
                        }, children: TABS.map((t) => {
                            const active = t.key === activeKey;
                            const dirty = drafts[t.key] !== undefined;
                            return (_jsxs("button", { type: "button", onClick: () => {
                                    setActiveKey(t.key);
                                    setResetArmed(null);
                                }, className: `ws-tab${active ? " is-active" : ""}`, style: {
                                    fontSize: 12,
                                    padding: "6px 12px",
                                    position: "relative",
                                }, children: [t.label, dirty && (_jsx("span", { style: {
                                            marginLeft: 4,
                                            color: "var(--ink-3)",
                                            fontStyle: "italic",
                                            fontFamily: "var(--serif)",
                                        }, children: "\u00B7" }))] }, t.key));
                        }) }), _jsxs("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                        }, children: [_jsxs("div", { style: { minWidth: 0 }, children: [_jsx("div", { style: { fontSize: 12.5, color: "var(--ink-2)" }, children: activeTab.blurb }), _jsxs("div", { style: {
                                            fontSize: 11,
                                            color: "var(--ink-3)",
                                            fontFamily: "var(--mono)",
                                            marginTop: 2,
                                        }, children: [activeKey, savingKey === activeKey
                                                ? " · saving…"
                                                : resetting === activeKey
                                                    ? " · resetting…"
                                                    : lastSavedIso
                                                        ? ` · saved ${formatRelativeTime(lastSavedIso)}`
                                                        : ""] })] }), _jsx("button", { type: "button", onClick: () => void onResetClick(activeKey), disabled: resetting === activeKey, className: "ws-btn", style: {
                                    fontSize: 11.5,
                                    padding: "4px 10px",
                                    ...(resetArmed === activeKey
                                        ? {
                                            background: "color-mix(in oklab, oklch(78% 0.14 70) 18%, var(--paper))",
                                            borderColor: "oklch(60% 0.14 70)",
                                            color: "oklch(35% 0.13 70)",
                                        }
                                        : {}),
                                }, title: "Overwrite this template with the bundled seed", children: resetArmed === activeKey ? "Click again to confirm" : "Reset to seed" })] }), _jsx(PromptEditor, { value: valueFor(activeKey), onChange: (next) => onChange(activeKey, next), onBlur: () => onBlur(activeKey) }), !inline && onClose && (_jsxs("div", { className: "ws-actions", children: [_jsx("span", { className: "ws-actions__hint" }), _jsx("button", { type: "button", className: "ws-btn", onClick: onClose, children: "Close" })] }))] }))] }));
    if (inline)
        return body;
    return (_jsx("div", { style: modalBackdrop, children: _jsx("div", { onClick: (e) => e.stopPropagation(), style: {
                ...modalPanel,
                width: 880,
                maxHeight: "85vh",
                overflow: "auto",
            }, children: body }) }));
}
function formatRelativeTime(iso) {
    const t = Date.parse(iso);
    if (!Number.isFinite(t))
        return iso;
    const delta = Math.max(0, Date.now() - t);
    const sec = Math.round(delta / 1000);
    if (sec < 5)
        return "just now";
    if (sec < 60)
        return `${sec}s ago`;
    const min = Math.round(sec / 60);
    if (min < 60)
        return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24)
        return `${hr}h ago`;
    return new Date(t).toLocaleDateString();
}
void JOURNALIST_PROMPT_KEYS;
const modalBackdrop = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "grid",
    placeItems: "center",
    zIndex: 100,
};
const modalPanel = {
    background: "var(--paper)",
    border: "1px solid var(--hairline)",
    borderRadius: 10,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 12,
};
//# sourceMappingURL=JournalistPromptsPanel.js.map